import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import ComposeEmailModal from "../../components/ComposeEmailModal";
import Modal from "../../components/Modal";
import { assistantApi } from "../../services/api";
import { colorFor, initials } from "../../utils/format";
import { copyText } from "../../utils/download";

const EMPTY_CONTACT = {
  name: "",
  title: "",
  email: "",
  linkedin: "",
  kind: "recruiter",
};

export const CONTACT_GROUPS = [
  {
    kind: "recruiter",
    title: "Recruiters",
    hint: "Hire for this kind of role. Ask about your application.",
    findLabel: "Find recruiters",
  },
  {
    kind: "hiringManager",
    title: "Hiring managers",
    hint: "Lead the team you'd join. Talk about the work, not just the process.",
    findLabel: "Find hiring managers",
  },
  {
    kind: "team",
    title: "Senior people in this role",
    hint: "Do this job at a senior level. Ask for advice or a referral.",
    findLabel: "Find senior people",
  },
  { kind: "", title: "Other contacts", hint: "" },
];

const LEVEL_WORDS =
  /\b(new grad(uate)?s?|early[- ]career|entry[- ]level|junior|jr|senior|sr|intern(ship)?|university|graduate|20\d\d)\b/gi;

// "Software Engineer, New Grad (2026)" -> "Software Engineer", for people searches.
function roleOf(title) {
  const parts = title
    .split(/\s[-\u2013\u2014|:]\s|[,()]/)
    .map((p) => p.replace(LEVEL_WORDS, "").replace(/\s+/g, " ").trim());
  return parts.find((p) => /[a-z]{3}/i.test(p)) || title;
}

const KIND_LABELS = Object.fromEntries(
  CONTACT_GROUPS.filter((g) => g.kind).map((g) => [
    g.kind,
    g.title.replace(/s$/, ""),
  ]),
);

// Fallback searches, used only when the automatic people finder isn't configured on the server:
// LinkedIn's own people search, and a Google "x-ray" search whose results are LinkedIn profiles.
export function buildPeopleSearches(job) {
  const role = roleOf(job.title || "");
  const company = job.company || "";
  const early =
    /new grad|early career|entry level|university|graduate|intern/i.test(
      `${job.title} ${job.employmentType}`,
    );
  const place =
    job.location && !/remote/i.test(job.location)
      ? ` ${job.location.split(",")[0]}`
      : "";
  const linkedin = (keywords) =>
    `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(keywords)}`;
  const google = (query) =>
    `https://www.google.com/search?q=${encodeURIComponent(query)}`;
  const site = `site:linkedin.com/in/ "${company}"`;
  const firstWord = role.split(/\s+/)[0] || role;
  return [
    {
      kind: "recruiter",
      label: `${early ? "University recruiters" : "Recruiters"} at ${company}`,
      linkedinUrl: linkedin(
        `${early ? "university recruiter" : "recruiter talent acquisition"} ${company}${place}`,
      ),
      googleUrl: google(
        `${site} (${early ? '"university recruiter" OR "early career recruiter" OR ' : ""}recruiter OR "talent acquisition" OR sourcer)${place}`,
      ),
    },
    {
      kind: "hiringManager",
      label: `Hiring managers for ${role} at ${company}`,
      linkedinUrl: linkedin(`${role} manager ${company}${place}`),
      googleUrl: google(
        `${site} ("${role} manager" OR "director of ${firstWord}" OR "head of ${firstWord}")${place}`,
      ),
    },
    {
      kind: "team",
      label: `Senior ${role}s at ${company}`,
      linkedinUrl: linkedin(`senior ${role} ${company}${place}`),
      googleUrl: google(`${site} "Senior ${role}"${place}`),
    },
  ];
}

export function linkedinSlug(url) {
  const match = String(url || "").match(/linkedin\.com\/in\/([^\s/?#,]+)/i);
  return match ? decodeURIComponent(match[1]).toLowerCase() : "";
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactCard({
  contact,
  onEmail,
  onDelete,
  onSaveEmail,
  subtitle,
}) {
  const { toast } = useApp();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [addingEmail, setAddingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState("");

  const saveEmail = async () => {
    const trimmed = emailValue.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) return;
    if (await onSaveEmail?.(contact, trimmed)) {
      setAddingEmail(false);
      setEmailValue("");
    }
  };
  return (
    <div className="contact-card">
      <div className="d-flex gap-3 align-items-start">
        <div className="avatar" style={{ background: colorFor(contact.name) }}>
          {initials(contact.name)}
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="fw-medium">{contact.name}</span>
            {contact.emailed && (
              <span className="badge bg-primary-subtle text-primary-emphasis">
                <i className="bi bi-check" /> Emailed
              </span>
            )}
            {contact.why && <span className="tag">{contact.why}</span>}
          </div>
          <div className="small text-muted">
            {subtitle || contact.title || KIND_LABELS[contact.kind] || ""}
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2 mt-2 small">
            {contact.email ? (
              <button
                className="btn btn-link btn-sm p-0 text-decoration-none text-truncate"
                onClick={async () =>
                  toast(
                    (await copyText(contact.email))
                      ? "Email copied"
                      : "Couldn't copy",
                    "info",
                  )
                }
                title="Copy email"
              >
                <i className="bi bi-envelope me-1" />
                {contact.email}
                <i className="bi bi-copy ms-1 small text-muted" />
              </button>
            ) : onSaveEmail && addingEmail ? (
              <span className="d-inline-flex gap-1 align-items-center">
                <input
                  className="form-control form-control-sm"
                  type="email"
                  placeholder="jane@company.com"
                  aria-label={`Email for ${contact.name}`}
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveEmail()}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn btn-sm btn-primary flex-shrink-0"
                  onClick={saveEmail}
                  disabled={!EMAIL_RE.test(emailValue.trim().toLowerCase())}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-light flex-shrink-0"
                  onClick={() => setAddingEmail(false)}
                >
                  Cancel
                </button>
              </span>
            ) : onSaveEmail ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={() => setAddingEmail(true)}
              >
                <i className="bi bi-envelope-plus me-1" />
                Add email
              </button>
            ) : (
              <span className="text-muted">No email added yet</span>
            )}
            {contact.linkedin && (
              <a
                href={contact.linkedin}
                target="_blank"
                rel="noreferrer"
                className="btn btn-link btn-sm p-0 text-decoration-none"
              >
                <i className="bi bi-linkedin me-1" />
                View LinkedIn profile
              </a>
            )}
          </div>
        </div>
        <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
          <button
            className={`btn btn-sm ${contact.emailed ? "btn-light" : "btn-primary"}`}
            onClick={onEmail}
          >
            {contact.emailed ? "Follow up" : contact.email ? "Email" : "Draft"}
          </button>
          {onDelete &&
            (confirmingDelete ? (
              <span className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => {
                    setConfirmingDelete(false);
                    onDelete(contact);
                  }}
                >
                  Remove
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setConfirmingDelete(false)}
                >
                  Keep
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-light text-danger"
                onClick={() => setConfirmingDelete(true)}
                title={`Remove ${contact.name}`}
                aria-label={`Remove ${contact.name}`}
              >
                <i className="bi bi-trash" />
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}

// One person the finder turned up: real name/headline from the search index, one click to
// their LinkedIn profile, one click to save them as a contact on this job.
function PersonRow({ person, added, onAdd }) {
  return (
    <div className="d-flex gap-3 align-items-start py-2 border-top">
      <div className="avatar" style={{ background: colorFor(person.name) }}>
        {initials(person.name)}
      </div>
      <div className="flex-grow-1 min-w-0">
        <div className="fw-medium">{person.name}</div>
        {person.headline && (
          <div className="small text-muted">{person.headline}</div>
        )}
        {!person.headline && person.snippet && (
          <div className="small text-muted">{person.snippet}</div>
        )}
      </div>
      <div className="d-flex flex-column flex-sm-row gap-1 flex-shrink-0">
        <a
          className="btn btn-sm btn-outline-primary"
          href={person.linkedin}
          target="_blank"
          rel="noreferrer"
        >
          <i className="bi bi-linkedin me-1" /> View Profile
        </a>
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={onAdd}
          disabled={added}
        >
          {added ? (
            <>
              <i className="bi bi-check me-1" /> Added
            </>
          ) : (
            <>
              <i className="bi bi-plus-lg me-1" /> Add
            </>
          )}
        </button>
      </div>
    </div>
  );
}

const INITIAL_SHOWN = 3;
const MORE_SHOWN = 5;

function ContactsTab({ job }) {
  const { completeStep, updateJob, toast } = useApp();
  const [composeFor, setComposeFor] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);
  const [found, setFound] = useState({}); // kind -> { people, fetchedNum, exhausted, cached }
  const [shown, setShown] = useState({}); // kind -> how many people are visible
  const [finding, setFinding] = useState(""); // kind currently loading (first load or Find more)
  const [finderErrors, setFinderErrors] = useState({});
  const [notConfigured, setNotConfigured] = useState(false);
  const autoLoadedFor = useRef("");
  const existingSlugs = useMemo(
    () =>
      new Set(
        (job.contacts || [])
          .map((c) => linkedinSlug(c.linkedin))
          .filter(Boolean),
      ),
    [job.contacts],
  );
  const fallbackSearches = useMemo(() => buildPeopleSearches(job), [job]);

  // Loads (or loads more of) one kind of person. Resolves to the result, or null on failure.
  const loadPeople = async (kind, num) => {
    setFinding(kind);
    setFinderErrors((e) => ({ ...e, [kind]: "" }));
    try {
      const result = await assistantApi.people(job.id, kind, num);
      if (!result.configured) {
        setNotConfigured(true);
        return null;
      }
      setNotConfigured(false);
      setFound((f) => ({ ...f, [kind]: result }));
      if (!result.people?.length) {
        setFinderErrors((e) => ({
          ...e,
          [kind]:
            "No public LinkedIn profiles came up for this one. Try the search links below, or check the company name on the job.",
        }));
      }
      return result;
    } catch (err) {
      setFinderErrors((e) => ({
        ...e,
        [kind]: err.message || "Couldn't find people right now. Try again.",
      }));
      return null;
    } finally {
      setFinding("");
    }
  };

  // People appear on their own when the tab opens - no button to press first.
  useEffect(() => {
    if (autoLoadedFor.current === job.id) return;
    autoLoadedFor.current = job.id;
    setFound({});
    setShown({});
    for (const group of CONTACT_GROUPS.filter((g) => g.kind))
      loadPeople(group.kind, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  // Show 5 more; fetch a bigger batch from the finder only when we've run out locally.
  const findMore = async (kind) => {
    const result = found[kind];
    if (!result) return;
    const target = (shown[kind] || INITIAL_SHOWN) + MORE_SHOWN;
    setShown((s) => ({ ...s, [kind]: target }));
    if (result.people.length >= target || result.exhausted) return;
    await loadPeople(kind, Math.min(40, (result.fetchedNum || 10) + 10));
  };

  const deleteContact = async (contact) => {
    const saved = await updateJob(job.id, {
      contacts: job.contacts.filter((c) => c.id !== contact.id),
    });
    if (saved) toast(`Removed ${contact.name}`);
  };

  const saveContactEmail = async (contact, email) => {
    const saved = await updateJob(job.id, {
      contacts: job.contacts.map((c) =>
        c.id === contact.id ? { ...c, email } : c,
      ),
    });
    if (saved) toast(`Email saved for ${contact.name}`);
    return Boolean(saved);
  };

  const addPerson = async (person, kind) => {
    const saved = await completeStep(
      job.id,
      "contacts",
      `Added ${person.name}`,
      {
        contacts: [
          ...job.contacts,
          {
            name: person.name,
            title: person.headline || "",
            email: "",
            linkedin: person.linkedin,
            kind,
          },
        ],
      },
    );
    if (saved) toast(`Added ${person.name}`);
  };

  const addManual = async () => {
    if (!newContact.name.trim()) return;
    const saved = await completeStep(
      job.id,
      "contacts",
      `Added ${newContact.name}`,
      {
        contacts: [
          ...job.contacts,
          { ...newContact, email: newContact.email.trim() },
        ],
      },
    );
    if (!saved) return;
    closeAdd();
  };

  const closeAdd = () => {
    setAdding(false);
    setNewContact(EMPTY_CONTACT);
  };

  const updateField = (key, value) =>
    setNewContact((c) => ({ ...c, [key]: value }));

  const composeContact =
    composeFor && job.contacts.find((c) => c.id === composeFor);

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <h2 className="h6 mb-1">People to reach out to</h2>
            <p className="text-muted small mb-0">
              Find real people at {job.company || "this company"}, open their
              LinkedIn profile, add the right ones, then draft an email in your
              voice. Plateo doesn't provide email addresses - a profile often
              hints at one, or look it up yourself with{" "}
              <a href="https://hunter.io" target="_blank" rel="noreferrer">
                Hunter.io
              </a>{" "}
              or{" "}
              <a href="https://www.apollo.io" target="_blank" rel="noreferrer">
                Apollo.io
              </a>
              .
            </p>
          </div>
          <button
            className="btn btn-sm btn-light"
            onClick={() => setAdding(true)}
          >
            <i className="bi bi-plus-lg me-1" /> Add manually
          </button>
        </div>

        {notConfigured && (
          <div className="alert alert-warning small py-2">
            The automatic people finder isn't turned on for this server yet, so
            use the search links under each group for now - they open the same
            people on LinkedIn and Google.
          </div>
        )}

        <div className="d-flex flex-column gap-3 mb-3">
          {CONTACT_GROUPS.filter((g) => g.kind).map((group) => {
            const result = found[group.kind];
            const fallback = fallbackSearches.find(
              (s) => s.kind === group.kind,
            );
            const showFallback =
              notConfigured || Boolean(finderErrors[group.kind]);
            const visibleCount = shown[group.kind] || INITIAL_SHOWN;
            const visible = result?.people?.slice(0, visibleCount) || [];
            const canFindMore =
              result?.people?.length > 0 &&
              (visibleCount < result.people.length || !result.exhausted);
            const allShown = result?.people?.length > 0 && !canFindMore;
            const loading = finding === group.kind;
            return (
              <div key={group.kind} className="border rounded p-3">
                <div>
                  <h3 className="h6 mb-0" style={{ fontSize: 14 }}>
                    {group.title}
                  </h3>
                  <span className="small text-muted">{group.hint}</span>
                </div>

                {loading && !result && (
                  <div className="small text-muted mt-2">
                    <span className="spinner-border spinner-border-sm me-1" />{" "}
                    Finding people at {job.company}…
                  </div>
                )}

                {finderErrors[group.kind] && (
                  <div className="form-text text-danger mt-2">
                    {finderErrors[group.kind]}{" "}
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 text-decoration-none align-baseline"
                      onClick={() => loadPeople(group.kind, 10)}
                    >
                      Try again
                    </button>
                  </div>
                )}

                {visible.length > 0 && (
                  <div className="mt-1">
                    {visible.map((person) => (
                      <PersonRow
                        key={person.linkedin}
                        person={person}
                        added={existingSlugs.has(linkedinSlug(person.linkedin))}
                        onAdd={() => addPerson(person, group.kind)}
                      />
                    ))}
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                      {canFindMore && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => findMore(group.kind)}
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" />{" "}
                              Finding more…
                            </>
                          ) : (
                            <>
                              <i className="bi bi-plus-lg me-1" /> Find more
                            </>
                          )}
                        </button>
                      )}
                      {allShown && (
                        <span className="form-text">
                          That&rsquo;s everyone we could find for this one.
                        </span>
                      )}
                    </div>
                    <div className="form-text mt-1">
                      Public profiles found on the web
                      {result.cached
                        ? " (saved copy - this company was looked up recently)"
                        : ""}
                      . Check a profile before adding - titles change.
                    </div>
                  </div>
                )}

                {showFallback && fallback && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <a
                      className="btn btn-sm btn-outline-primary"
                      href={fallback.linkedinUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="bi bi-linkedin me-1" /> Search on LinkedIn
                    </a>
                    <a
                      className="btn btn-sm btn-light"
                      href={fallback.googleUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="bi bi-google me-1" /> Find profiles on
                      Google
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {job.contacts.length === 0 ? (
          <div className="text-center py-4">
            <div className="empty-state-icon mx-auto mb-3">
              <i className="bi bi-person-lines-fill" />
            </div>
            <p className="text-muted small mb-0">
              No one added yet. Add 2–3 people from the lists above who look
              right.
            </p>
          </div>
        ) : (
          CONTACT_GROUPS.map((group) => {
            const people = job.contacts.filter(
              (c) => (c.kind || "") === group.kind,
            );
            if (!people.length) return null;
            return (
              <div key={group.kind || "other"} className="mb-3">
                <div className="d-flex flex-wrap align-items-baseline gap-2 mb-2">
                  <h3 className="h6 mb-0" style={{ fontSize: 14 }}>
                    {group.title}
                  </h3>
                  {group.hint && (
                    <span className="small text-muted">{group.hint}</span>
                  )}
                </div>
                <div className="d-flex flex-column gap-2">
                  {people.map((c) => (
                    <ContactCard
                      key={c.id}
                      contact={c}
                      onEmail={() => setComposeFor(c.id)}
                      onDelete={deleteContact}
                      onSaveEmail={saveContactEmail}
                    />
                  ))}
                </div>
              </div>
            );
          })
        )}

        <p className="small text-muted mb-0">
          Outreach templates live in{" "}
          <Link to="/documents#email-templates">Documents</Link> - one style per
          recipient type, so emails sound like you.
        </p>
      </div>

      {composeContact && (
        <ComposeEmailModal
          key={composeContact.id}
          job={job}
          contact={composeContact}
          onClose={() => setComposeFor(null)}
        />
      )}

      <Modal
        show={adding}
        onClose={closeAdd}
        title="Add a contact"
        footer={
          <>
            <button className="btn btn-light" onClick={closeAdd}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={addManual}
              disabled={!newContact.name.trim()}
            >
              Add contact
            </button>
          </>
        }
      >
        <p className="small text-muted">
          Found someone on LinkedIn? Add them here. Email is optional - add it
          only if you found it yourself.
        </p>
        {[
          ["name", "Name *", "Jane Doe"],
          ["title", "Role", "Technical Recruiter"],
          ["email", "Email (optional)", "jane@company.com"],
          ["linkedin", "LinkedIn URL", "https://linkedin.com/in/…"],
        ].map(([key, label, ph]) => (
          <div className="mb-3" key={key}>
            <label
              className="form-label small fw-medium"
              htmlFor={`contact-${key}`}
            >
              {label}
            </label>
            <input
              id={`contact-${key}`}
              className="form-control"
              placeholder={ph}
              value={newContact[key]}
              onChange={(e) => updateField(key, e.target.value)}
            />
          </div>
        ))}
        <div className="mb-1">
          <label className="form-label small fw-medium" htmlFor="contact-kind">
            Type
          </label>
          <select
            id="contact-kind"
            className="form-select"
            value={newContact.kind}
            onChange={(e) => updateField("kind", e.target.value)}
          >
            <option value="recruiter">Recruiter</option>
            <option value="hiringManager">Hiring manager</option>
            <option value="team">Senior in this role</option>
            <option value="">Other</option>
          </select>
        </div>
      </Modal>
    </div>
  );
}

export default ContactsTab;
